// RotateGizmoRings — the rotate gizmo's LOOK, rebuilt to PlayCanvas's.
//
// three's rotate visual is a single thin hairline arc per axis and nothing else:
// no completion on grab, no angle guide, no sense of how far you have turned.
// PlayCanvas' RotateGizmo (engine/src/extras/gizmo/rotate-gizmo.js) draws:
//
//   • a 180° arc per axis, camera-facing, tubeRadius 0.01
//   • a screen-facing ring outside them, and a translucent ball at the centre
//     for free rotation about the view axis
//   • on grab: the SELECTED ring completes into a full circle and the others
//     hide, so the thing you are turning is unambiguous (`_drag`, dragMode
//     'hide')
//   • two guide lines from the centre — one faded at the angle you STARTED
//     from, one solid at the angle you are at now (`_angleGuide`)
//
// WHY THIS COEXISTS WITH TransformControls RATHER THAN REPLACING IT
// three's invisible rotate PICKERS already do hover + drag detection correctly.
// So this hides three's rotate DRAWING (helper.gizmo.rotate) and paints over the
// top at the pickers' own radii: what you see and what you grab stay the same
// handle, and none of the picking, camera stand-down or undo behaviour that
// transform-gizmo.spec.js pins has to be rewritten.
//
// That correspondence is the rule this file lives by — EVERY picker three keeps
// gets something drawn for it, or the gizmo grows an invisible-but-grabbable
// handle. three has five: X/Y/Z toruses, the 'E' screen-facing torus, and the
// 'XYZE' centre sphere. All five are drawn here, and each one's visibility
// mirrors three's own showX/showY/showZ gating (see `visible` below).
//
// Everything here runs in useFrame and mutates objects in place — no React state
// on the hot path, per CLAUDE.md.

import React, { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

import {
  RING, RING_COLOR, RING_COLOR_HOVER, SPHERE_COLOR, SPHERE_OPACITY,
} from '../lib/rotateGizmoColors.js'
import { gizmoWorldSize } from '../lib/gizmoScaleFeel'
import { planeBasis, arcAngle } from '../lib/rotateArcAngle.js'
import { BASE_EULER, sectorSpin } from '../lib/rotateRingSector.js'

const AXES = ['x', 'y', 'z']
const UNIT = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] }
const ORIGIN = [0, 0, 0]
const IDENTITY = new THREE.Quaternion()
const Z_AXIS = new THREE.Vector3(0, 0, 1)

// Per-frame scratch. Allocating these inside useFrame is exactly the per-frame
// garbage CLAUDE.md forbids on the render path.
const _camLocal = new THREE.Vector3()
const _eye = new THREE.Vector3()
const _v = new THREE.Vector3()
const _dir = new THREE.Vector3()
const _side = new THREE.Vector3()
const _up = new THREE.Vector3()
const _q = new THREE.Quaternion()
const _m = new THREE.Matrix4()

/**
 * three's internal gizmo object — the one carrying `.gizmo` / `.picker` /
 * `.helper`, each a `{translate, rotate, scale}` group.
 *
 * `controls.getHelper()` returns the ROOT, not this: the root holds the gizmo
 * and the drag plane as children. Reaching for `getHelper().gizmo` silently
 * yields undefined, which is exactly how three's rotate arcs kept drawing
 * underneath ours — hence one resolver, used by both the hide and the test that
 * pins it, rather than the same guess written twice.
 *
 * Prefers the private field, falls back to finding it among the root's children
 * so a rename doesn't break us, and returns null (never a guess) if neither
 * shape is there.
 */
export function threeGizmoGroups(controls) {
  if (!controls) return null
  const direct = controls._gizmo
  if (direct?.gizmo && direct?.picker) return direct
  const root = controls.getHelper?.()
  return root?.children?.find(c => c?.gizmo && c?.picker) ?? null
}

/**
 * three's axis label → the handle drawn for it.
 *
 * 'E' (screen-facing ring) and 'XYZE' (centre ball) both rotate about the VIEW
 * axis, so both map to the facing ring — a sphere has no arc to sweep.
 */
export function handleFor(threeAxis) {
  const a = String(threeAxis ?? '').toLowerCase()
  if (a === 'x' || a === 'y' || a === 'z') return a
  if (a === 'e' || a === 'xyze') return 'f'
  return null
}

export default function RotateGizmoRings({
  // The gizmo's world position (the proxy TransformControls drives).
  target,
  // Which axes the adapter allows: { x, y, z } booleans, or null to draw none.
  axes,
  // three's controls, for size/axis/dragging — as a REF, read per frame. Passing
  // the value would tie these visuals to React render timing: the ref is filled
  // in TransformGizmo's build effect, and a render that happens to come first
  // hands over null, which every read below then swallows silently.
  controlsRef,
  // TransformGizmo's rotate-drag REF: `.current` is
  // { axis, ring, startAngle, total, … } while rotating, null otherwise. A ref,
  // not a value, so a drag starting or ending costs no React render.
  dragRef,
}) {
  const { camera } = useThree()
  const groupRef = useRef(null)

  // Built once. The half arc and the full circle are two pre-built geometries a
  // mesh is swapped between — cheaper and steadier than rebuilding one mid-drag.
  const built = useMemo(() => {
    const half = new THREE.TorusGeometry(RING.radius, RING.tube, 8, 96, RING.sector)
    const full = new THREE.TorusGeometry(RING.radius, RING.tube, 8, 160, Math.PI * 2)
    // The facing ring is always a full circle — a sector has no near half to
    // aim at when the ring already lies in the screen plane.
    const facingGeom = new THREE.TorusGeometry(RING.facingRadius, RING.tube, 8, 160)

    // Always on top of the scene, like every gizmo: depth-testing a thin ring
    // against an avatar hides exactly the half you are reaching for.
    const ringMat = (color) => new THREE.MeshBasicMaterial({
      color, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
      toneMapped: false,
    })

    const rings = {}
    for (const a of AXES) {
      const mesh = new THREE.Mesh(half, ringMat(RING_COLOR[a]))
      mesh.renderOrder = 10_000
      mesh.rotation.set(...BASE_EULER[a])
      rings[a] = mesh
    }
    const facing = new THREE.Mesh(facingGeom, ringMat(RING_COLOR.f))
    facing.renderOrder = 10_000
    rings.f = facing

    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(RING.sphereRadius, 24, 16),
      new THREE.MeshBasicMaterial({
        color: SPHERE_COLOR, transparent: true, opacity: SPHERE_OPACITY,
        depthTest: false, depthWrite: false, toneMapped: false,
      }),
    )
    // Under the rings: the ball is a backdrop for them, not a competitor.
    sphere.renderOrder = 9_999

    // Angle guides as thin QUADS, not THREE.Line segments.
    //
    // A Line here is one device pixel — WebGL ignores `linewidth` entirely — and
    // one pixel is genuinely unreadable against the floor grid it crosses, which
    // a capture confirmed: the guides drew, and you could not see them. A guide
    // that reports "visible" while telling the user nothing about how far they
    // have turned is the worst of both. A unit quad, scaled to the ring's radius
    // and oriented per frame, is the same shape with a width we control.
    //
    // Translated so the quad runs from the origin along +X — the guide starts at
    // the gizmo centre, so the geometry should too.
    const guideGeom = new THREE.PlaneGeometry(1, 1).translate(0.5, 0, 0)
    const mkGuide = (opacity) => {
      const m = new THREE.Mesh(guideGeom, new THREE.MeshBasicMaterial({
        // White, deliberately NOT the ring's colour: a guide the same hue as the
        // ring it measures against disappears into it.
        color: 0xffffff, transparent: true, opacity,
        depthTest: false, depthWrite: false, toneMapped: false,
        side: THREE.DoubleSide, // it is edge-on half the time
      }))
      m.renderOrder = 10_001
      m.visible = false
      return m
    }
    // PlayCanvas: the line you started from is faded, the one you are at is not.
    return {
      half, full, rings, sphere, guideGeom,
      guideStart: mkGuide(0.35), guideEnd: mkGuide(0.95),
    }
  }, [])

  // Debug/test seam, per CLAUDE.md's window.__* convention. The rings are
  // siblings of three's helper rather than children of it, so they are not
  // reachable by walking __TRANSFORM_GIZMO__.controls — same reason __TORCH__ and
  // __CAMERA_CAGE_GIZMO__ each get their own global.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__ROTATE_RINGS__ = {
      get group() { return groupRef.current },
      rings: built.rings,
      sphere: built.sphere,
      guideStart: built.guideStart,
      guideEnd: built.guideEnd,
      // Whether the rings can actually see three's controls. Worth reporting
      // because losing them is SILENT — `controls?.dragging` on null is just
      // undefined, so the gizmo keeps drawing its resting state through a drag
      // and nothing complains.
      get seesControls() { return Boolean(controlsRef?.current) },
    }
    return () => { delete window.__ROTATE_RINGS__ }
  }, [built, controlsRef])

  useFrame(() => {
    const group = groupRef.current
    if (!group || !target) return
    const controls = controlsRef?.current ?? null
    const drag = dragRef?.current ?? null

    // Constant screen size, using the SAME factor three applies to its own
    // handles (gizmoWorldSize mirrors TransformControlsGizmo.updateMatrixWorld),
    // so these rings sit exactly on the invisible pickers.
    const size = gizmoWorldSize({
      cameraDistance: camera.position.distanceTo(target.position),
      fovDeg: camera.fov,
      zoom: camera.zoom,
      size: controls?.size ?? 1,
      orthoHeight: camera.isOrthographicCamera ? (camera.top - camera.bottom) : null,
    })
    group.position.copy(target.position)
    group.scale.setScalar(size ?? 1)
    // World-space rings unless the gizmo is in local space, where they follow
    // the object. Everything below is computed in the group's LOCAL frame, so
    // the same code serves both.
    group.quaternion.copy(controls?.space === 'local' ? target.quaternion : IDENTITY)
    group.updateMatrixWorld()
    _q.copy(group.quaternion).invert()

    // Camera position and view direction, in group-local coordinates.
    group.worldToLocal(_camLocal.copy(camera.position))
    _eye.copy(_camLocal).normalize()

    const dragging = Boolean(controls?.dragging && drag)
    // `ring` is the handle LABEL ('x'|'y'|'z'|'f'). drag.axis is the world-space
    // axis VECTOR the maths uses — indexing the meshes with that would silently
    // match nothing and hide every ring mid-drag.
    const dragRing = dragging ? (drag.ring || null) : null
    const hovered = handleFor(controls?.axis)

    // three gates its own handle visibility by SUBSTRING on the handle name:
    // 'X' needs showX and so on, and a separate clause gates any name containing
    // 'E' on showX && showY && showZ. So both view-axis handles ('E' and 'XYZE')
    // are grabbable only when every axis is allowed. Mirroring that exactly is
    // what keeps drawn == grabbable — an adapter capped to one axis must not
    // show a facing ring three refuses to hit-test.
    const allAxes = Boolean(axes?.x && axes?.y && axes?.z)
    const shown = {
      x: Boolean(axes?.x), y: Boolean(axes?.y), z: Boolean(axes?.z), f: allAxes,
    }

    for (const a of [...AXES, 'f']) {
      const mesh = built.rings[a]
      if (!shown[a]) { mesh.visible = false; continue }

      // While dragging, only the handle being turned is drawn — and as a FULL
      // circle, so the sweep is readable all the way round (PlayCanvas `_drag`).
      mesh.visible = dragging ? a === dragRing : true
      if (!mesh.visible) continue

      if (a === 'f') {
        // Lay the facing ring in the screen plane: its hole points at the eye.
        mesh.quaternion.setFromUnitVectors(Z_AXIS, _eye)
      } else if (dragging) {
        mesh.geometry = built.full
      } else {
        mesh.geometry = built.half
        // Aim the 180° sector at the camera, so the drawn half is always the
        // near one (PlayCanvas `_shapesLookAtCamera`). Both conventions standing
        // between the geometry's own angle and a world direction are measured in
        // rotateRingSector.js rather than assumed here.
        const axisVec = UNIT[a]
        const camAngle = arcAngle([_camLocal.x, _camLocal.y, _camLocal.z], ORIGIN, axisVec)
        mesh.rotation.set(...BASE_EULER[a])
        mesh.rotateZ(sectorSpin(a, axisVec, camAngle))
      }

      const active = dragging ? a === dragRing : a === hovered
      mesh.material.color.setHex(active ? RING_COLOR_HOVER[a] : RING_COLOR[a])
    }

    // The centre ball follows three's 'XYZE' picker, which its substring rule
    // hides unless every axis is shown. Hidden mid-drag with the other handles.
    built.sphere.visible = allAxes && !dragging
    const sphereHot = String(controls?.axis ?? '').toUpperCase() === 'XYZE'
    built.sphere.material.color.setHex(sphereHot ? RING_COLOR_HOVER.f : SPHERE_COLOR)

    // ── Angle guide ──
    built.guideStart.visible = dragging
    built.guideEnd.visible = dragging
    if (dragging) {
      // drag.axis is the WORLD rotation axis (the eye vector for the facing
      // handle). Rotating it into the group's frame serves every case with one
      // expression — in world space the group is identity, and in local space
      // this hands back the unrotated axis the rings themselves use.
      _v.set(drag.axis[0], drag.axis[1], drag.axis[2]).applyQuaternion(_q).normalize()
      const { u, v } = planeBasis([_v.x, _v.y, _v.z])
      const r = dragRing === 'f' ? RING.facingRadius : RING.radius
      const at = (angle, quad) => {
        // The quad runs from the origin along its own +X, so orienting it is a
        // change of basis: +X along the guide, +Z on the rotation axis (the quad
        // must lie IN the ring's plane), +Y completing it. Cheaper and steadier
        // than rewriting vertex positions, and it scales without distorting the
        // width.
        _dir.set(u[0], u[1], u[2]).multiplyScalar(Math.cos(angle))
          .addScaledVector(_side.set(v[0], v[1], v[2]), Math.sin(angle))
          .normalize()
        _up.copy(_v).cross(_dir).normalize()
        _m.makeBasis(_dir, _up, _v)
        quad.quaternion.setFromRotationMatrix(_m)
        // Length is the ring's radius; width is a small multiple of the tube, so
        // the guide reads as a line without pretending to be another ring.
        quad.scale.set(r, RING.tube * 1.6, 1)
      }
      at(drag.startAngle, built.guideStart)
      at(drag.startAngle + drag.total, built.guideEnd)
    }
  })

  // Nothing to draw before a selection exists.
  if (!target || !axes) return null

  return (
    <group ref={groupRef}>
      {[...AXES, 'f'].map(a => <primitive key={a} object={built.rings[a]} />)}
      <primitive object={built.sphere} />
      <primitive object={built.guideStart} />
      <primitive object={built.guideEnd} />
    </group>
  )
}

